import { createAsyncThunk } from "@reduxjs/toolkit";
import { AxiosError } from "axios";
import {
	iNotifcation,
	iNotificationCentreCounts,
	iNotificationFilter,
	iNotificationPage,
	NotificationListSurface,
} from "../../types/notificationTypes";
import { iApiError, iThunkApi } from "../../types/commonTypes";
import { getAccessToken } from "../../helpers/authHelpers";
import { apiManager, isApiSuccess } from "../apiManager";
import { API_ENDPOINTS } from "../../constants/apiConstants";
import { DEFAULT_API_ERROR } from "../../constants/commonConstants";

export const NOTIFICATION_PAGE_LIMIT = 30;
export const NOTIFICATION_POPOVER_LIMIT = 10;
const MALFORMED_CURSOR_STATUS_CODE = 11640020;

const notificationListUrl = (filter: iNotificationFilter, limit: number, cursor?: string | null) => {
	const params = new URLSearchParams({ limit: String(limit) });
	if (filter.category) params.set("category", filter.category);
	if (filter.unreadOnly) params.set("unreadOnly", "true");
	if (cursor) params.set("cursor", cursor);
	return `${API_ENDPOINTS.GET_NOTIFICATIONS_V2}?${params.toString()}`;
};

const getNotificationPage = async (filter: iNotificationFilter, limit: number, cursor?: string | null) => {
	const resp = await apiManager(notificationListUrl(filter, limit, cursor), {}, "GET", {
		Authorization: getAccessToken(),
	});
	return resp?.data?.data as iNotificationPage | undefined;
};

const isMalformedCursorError = (error: unknown) =>
	(error as AxiosError<iApiError>)?.response?.data?.statusCode === MALFORMED_CURSOR_STATUS_CODE;

export const getNotificationListThunk = createAsyncThunk<
	iNotificationPage & { appended: boolean },
	{ surface: NotificationListSurface; filter: iNotificationFilter; cursor?: string | null; limit: number },
	iThunkApi
>("notifications/get-list", async ({ filter, cursor, limit }, thunk) => {
	try {
		const page = await getNotificationPage(filter, limit, cursor);
		return { items: page?.items ?? [], nextCursor: page?.nextCursor ?? null, appended: !!cursor };
	} catch (error) {
		if (cursor && isMalformedCursorError(error)) {
			const page = await getNotificationPage(filter, limit);
			return { items: page?.items ?? [], nextCursor: page?.nextCursor ?? null, appended: false };
		}
		return thunk.rejectWithValue((error as AxiosError<iApiError>)?.response?.data ?? DEFAULT_API_ERROR);
	}
});

export const getNotificationCentreCountsThunk = createAsyncThunk<
	iNotificationCentreCounts | undefined,
	undefined,
	iThunkApi
>("notifications/centre-counts", async (_, thunk) => {
	const resp = await apiManager(API_ENDPOINTS.NOTIFICATION_CENTRE_COUNTS, {}, "GET", {
		Authorization: getAccessToken(),
	});
	if (!resp) {
		return;
	}
	if (!isApiSuccess(resp.status)) {
		return thunk.rejectWithValue(resp.data);
	}
	return resp.data?.data;
});

export const deleteNotification = createAsyncThunk<
	{ data: iNotifcation; message: string; statusCode: number },
	iNotifcation,
	iThunkApi
>("notifications/delete-one", async (notification, thunk) => {
	const accessToken = getAccessToken();
	const resp = await apiManager(API_ENDPOINTS.NOTIFICATION_REMOVE_ONE(notification.id), {}, "DELETE", {
		Authorization: accessToken,
	});
	if (!resp) {
		return;
	}
	if (!isApiSuccess(resp.status)) {
		return thunk.rejectWithValue(resp.data);
	}
	thunk.dispatch(getNotificationCentreCountsThunk());
	thunk.dispatch(getNotificationBadgeCount());
	return { ...resp.data, data: notification };
});

export const deleteNotificationsAll = createAsyncThunk<
	{ message: string; statusCode: number },
	undefined,
	iThunkApi
>("notifications/delete-all", async (_, thunk) => {
	const accessToken = getAccessToken();
	const resp = await apiManager(API_ENDPOINTS.NOTIFICATION_REMOVE_ALL, {}, "DELETE", {
		Authorization: accessToken,
	});
	if (!resp) {
		return;
	}
	if (!isApiSuccess(resp.status)) {
		return thunk.rejectWithValue(resp.data);
	}

	return resp.data;
});

export const markReadNotification = createAsyncThunk<
	{ notificationId: string; message: string; statusCode: number },
	string,
	iThunkApi
>("notifications/mark-read-one", async (notificationId, thunk) => {
	const accessToken = getAccessToken();
	const resp = await apiManager(API_ENDPOINTS.NOTIFICATION_MARK_READ(notificationId), {}, "PUT", {
		Authorization: accessToken,
	});
	if (!resp) {
		return;
	}
	if (!isApiSuccess(resp.status)) {
		return thunk.rejectWithValue(resp.data);
	}
	thunk.dispatch(getNotificationCentreCountsThunk());
	thunk.dispatch(getNotificationBadgeCount());
	return { ...resp.data, notificationId: notificationId };
});

export const markNotificationsAllRead = createAsyncThunk<
	{ message: string; statusCode: number },
	undefined,
	iThunkApi
>("notifications/all-read", async (_, thunk) => {
	const accessToken = getAccessToken();
	const resp = await apiManager(API_ENDPOINTS.NOTIFICATION_MARK_ALL_READ, {}, "PUT", {
		Authorization: accessToken,
	});
	if (!resp) {
		return;
	}
	if (!isApiSuccess(resp.status)) {
		return thunk.rejectWithValue(resp.data);
	}
	thunk.dispatch(getNotificationCentreCountsThunk());
	thunk.dispatch(getNotificationBadgeCount());
	return resp.data;
});

export const notificationCountThunk = createAsyncThunk<
	{
		aiDirectRequestNotificationCount: number;
		directRequestNotificationCount: number;
		inProgressNotificationCount: number;
		approvedNotificationCount: number;
		eventDirectoryNotificationCount: number;
		prepaidRequestNotificationCount: number;
		corporateEventRequestNotificationCount: number;
		corporateEventApprovedNotificationCount: number;
	},
	undefined,
	iThunkApi
>("notifications/count", async (_, thunk) => {
	const accessToken = getAccessToken();
	const resp = await apiManager(API_ENDPOINTS.NOTIFICATION_COUNTS, {}, "GET", {
		Authorization: accessToken,
	});
	if (!resp) {
		return;
	}
	if (!isApiSuccess(resp.status)) {
		return thunk.rejectWithValue(resp.data);
	}
	thunk.dispatch(getNotificationBadgeCount());
	return resp.data;
});

export const getNotificationBadgeCount = createAsyncThunk<
	| {
			notificationBadgeCount: number;
	  }
	| undefined,
	undefined,
	iThunkApi
>("notifications/badge-count", async (_, thunk) => {
	const accessToken = getAccessToken();
	const resp = await apiManager(API_ENDPOINTS.NOTIFICATION_BADGE_COUNT, {}, "GET", {
		Authorization: accessToken,
	});
	if (!resp) {
		return;
	}
	if (!isApiSuccess(resp.status)) {
		return thunk.rejectWithValue(resp.data);
	}
	const count = resp?.data?.data?.notificationBadgeCount;
	if (navigator?.setAppBadge) {
		if (count && count > 0) {
			navigator?.setAppBadge(count);
		} else {
			navigator?.clearAppBadge();
		}
	}
	return resp?.data?.data;
});

export const refreshNotificationsThunk = createAsyncThunk<void, undefined, iThunkApi>(
	"notifications/refresh",
	async (_, thunk) => {
		const { lists } = thunk.getState().notifications;
		const reloads = (Object.keys(lists) as NotificationListSurface[])
			.filter((surface) => lists[surface].status !== "idle")
			.map((surface) =>
				thunk.dispatch(
					getNotificationListThunk({
						surface,
						filter: lists[surface].filter,
						limit: surface === "popover" ? NOTIFICATION_POPOVER_LIMIT : NOTIFICATION_PAGE_LIMIT,
					}),
				),
			);
		await Promise.all([
			thunk.dispatch(getNotificationCentreCountsThunk()),
			thunk.dispatch(notificationCountThunk()),
			...reloads,
		]);
	},
);
